import { Controller } from "@hotwired/stimulus";

// Deterministic pseudo-random in [0, 1) from an integer seed + salt. Used to
// scatter pins that share a city centroid to stable, reproducible spots (so the
// dispersion is the same on every render instead of jumping with Math.random).
function hash01(seed, salt) {
  let h = Math.imul(seed | 0, 2654435761) ^ Math.imul(salt | 0, 40503);
  h = Math.imul(h ^ (h >>> 15), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// Renders the Locals professionals map with Leaflet. Leaflet itself is the
// vendored bundle exposed as window.L by packs/application.js (no CDN). Pins are
// handed in server-side as a JSON array value so there is no second round trip;
// Locals::MapController also serves the same data at /map.json for programmatic
// use.
export default class extends Controller {
  static values = {
    pins: Array,
    // [lat, lng] of the selected city, and the radius (km); set only when the city
    // filter is active. radius 0 = no radius filter -> fit to pins instead.
    center: { type: Array, default: [] },
    radius: { type: Number, default: 0 },
    // Center of the Netherlands — fallback when there are no pins.
    fallbackCenter: { type: Array, default: [52.1326, 5.2913] },
    fallbackZoom: { type: Number, default: 7 },
  };

  connect() {
    if (typeof L === "undefined") return;
    if (this.map) return;

    this.map = L.map(this.element);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(this.map);

    this.pinIcon = this.markerIcon();

    const bounds = [];
    const pins = (this.pinsValue || []).filter((p) => p.lat != null && p.lng != null);

    // Everyone in a city shares that city's centroid (geocode-free map, Decision
    // 0010), so without dispersion they all stack on one point. Count how many
    // share each coordinate; where >1, scatter each pin to a deterministic random
    // spot within a city-sized radius (seeded by id so pins don't jump on reload
    // and a ritual-circle ring never forms).
    const counts = {};
    pins.forEach((p) => {
      const key = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    pins.forEach((pin, index) => {
      let lat = pin.lat;
      let lng = pin.lng;
      const key = `${pin.lat.toFixed(5)},${pin.lng.toFixed(5)}`;
      if (counts[key] > 1) {
        const seed = pin.id != null ? pin.id : index;
        const SPREAD = 0.054; // ~6 km — spreads dense cities (Rotterdam/Amsterdam) right out
        const r = SPREAD * Math.sqrt(hash01(seed, 1)); // sqrt → uniform over the disc
        const theta = 2 * Math.PI * hash01(seed, 2);
        lat += r * Math.cos(theta);
        lng += (r * Math.sin(theta)) / Math.cos((pin.lat * Math.PI) / 180);
      }
      const marker = L.marker([lat, lng], { icon: this.pinIcon }).addTo(this.map);
      marker.bindPopup(this.popupHtml(pin));
      bounds.push([lat, lng]);
    });

    // Zoom: with a city + radius active, draw the radius circle and fit the map to
    // *it* so the zoom matches the chosen radius. Otherwise fit to the pins.
    const center = this.centerValue;
    const radiusKm = this.radiusValue;
    if (center.length === 2 && radiusKm > 0) {
      const circle = L.circle(center, {
        radius: radiusKm * 1000,
        color: "#1a1a1a",
        weight: 1,
        opacity: 0.35,
        fillColor: "#1a1a1a",
        fillOpacity: 0.05,
      }).addTo(this.map);
      this.map.fitBounds(circle.getBounds(), { padding: [16, 16] });
    } else if (bounds.length > 1) {
      this.map.fitBounds(bounds, { padding: [24, 24] });
    } else if (bounds.length === 1) {
      this.map.setView(bounds[0], 12);
    } else {
      this.map.setView(this.fallbackCenterValue, this.fallbackZoomValue);
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  // Black drop pin matching the Locals logo (locals.svg): the same teardrop path
  // + white circle. Anchored at the tip (bottom-center).
  markerIcon() {
    const svg =
      '<svg viewBox="0 0 72 108" xmlns="http://www.w3.org/2000/svg" width="30" height="45">' +
      '<path d="M36 6 C20 6 7 19 7 36 C7 56 36 102 36 102 C36 102 65 56 65 36 C65 19 52 6 36 6 Z" fill="#1a1a1a"/>' +
      '<circle cx="36" cy="34" r="11" fill="#ffffff"/>' +
      "</svg>";
    return L.divIcon({
      className: "locals-pin",
      html: svg,
      iconSize: [30, 45],
      iconAnchor: [15, 45],
      popupAnchor: [0, -42],
    });
  }

  popupHtml(pin) {
    const name = this.escape(pin.name);
    const profession = pin.profession
      ? `<br><span>${this.escape(pin.profession)}</span>`
      : "";
    const city = pin.city ? `<br><small>${this.escape(pin.city)}</small>` : "";
    const link = pin.url
      ? `<br><a href="${this.escape(pin.url)}">View profile</a>`
      : "";
    return `<div><strong>${name}</strong>${profession}${city}${link}</div>`;
  }

  escape(value) {
    if (value == null) return "";
    const span = document.createElement("span");
    span.textContent = value.toString();
    return span.innerHTML;
  }
}
